import type { IOptionItem } from "@/components/ui/dropdown/types";
import { DATA_SETTINGS_MESSAGE } from "@/constants/message";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  TimeseriesFormat,
  ValidateTimeseriesAttributeResponse
} from "@/types/dataset";
import { getTimeseriesFormatFromLabel } from "@/utils/datasetAttributes";

export const TIMESERIES_FORMAT_OPTIONS: IOptionItem[] = [
  { label: "Date", value: TimeseriesFormat.DATE },
  { label: "Date & Time", value: TimeseriesFormat.DATETIME },
  { label: "Year", value: TimeseriesFormat.YEAR },
  { label: "Year-Month", value: TimeseriesFormat.YEAR_MONTH },
  { label: "Year-Month-Day", value: TimeseriesFormat.YEAR_MONTH_DAY },
  { label: "Year-Quarter", value: TimeseriesFormat.YEAR_QUARTER },
  { label: "Year-Week", value: TimeseriesFormat.YEAR_WEEK },
  { label: "Quarter", value: TimeseriesFormat.QUARTER },
  { label: "Month", value: TimeseriesFormat.MONTH },
  { label: "Week of Year", value: TimeseriesFormat.WEEK_OF_YEAR },
  { label: "Day of Month", value: TimeseriesFormat.DAY_OF_MONTH },
  { label: "Day Name", value: TimeseriesFormat.DAY_NAME },
  { label: "Hour", value: TimeseriesFormat.HOUR },
  { label: "Minute", value: TimeseriesFormat.MINUTE },
  { label: "Other", value: TimeseriesFormat.OTHER }
];

export type TimeAttributeRowValue = {
  key: string;
  attributeId?: string;
  format?: TimeseriesFormat;
  /** The user changed this row, so it has to be checked again. */
  isTouched?: boolean;
};

const TIME_ATTRIBUTE_DATA_TYPES = new Set<DataSchemaType>([
  DataSchemaType.TEXT,
  DataSchemaType.TEXT2,
  DataSchemaType.INT,
  DataSchemaType.FLOAT,
  DataSchemaType.DATE,
  DataSchemaType.TIME,
  DataSchemaType.DATE_TIME
]);

export const getTimeAttributeOptions = (
  schema: DatasetAttributeSchema[]
): IOptionItem[] =>
  schema
    .filter(
      (attribute) =>
        !attribute.is_spatial &&
        !attribute.is_reference_id &&
        !attribute.is_reference_geo &&
        TIME_ATTRIBUTE_DATA_TYPES.has(attribute.data_type)
    )
    .map((attribute) => ({
      label: attribute.attribute_name,
      value: attribute.id
    }));

/** Saved time attributes are flagged `group_by_attr`; their format sits on `data_label`. */
export const getSavedTimeAttributeRows = (
  schema: DatasetAttributeSchema[]
): TimeAttributeRowValue[] =>
  schema
    .filter((attribute) => attribute.group_by_attr)
    .map((attribute) => ({
      key: attribute.id,
      attributeId: attribute.id,
      format: getTimeseriesFormatFromLabel(attribute.data_label)
    }));

export const hasNumericAttribute = (schema: DatasetAttributeSchema[]) =>
  schema.some(
    (attribute) =>
      attribute.data_type === DataSchemaType.INT ||
      attribute.data_type === DataSchemaType.FLOAT
  );

/** The check explains a failure itself; fall back when it says nothing. */
export const getTimeseriesErrorMessage = (
  result: ValidateTimeseriesAttributeResponse | undefined
): string | undefined => {
  if (!result || result.valid) return undefined;
  // BE puts the attribute name in front ("BULAN: ..."); the row already shows it.
  const message = result.message?.replace(/^[^:]*: /, "");
  return message || DATA_SETTINGS_MESSAGE.TIMESERIES_ERROR_FALLBACK;
};
